// Ta'til — `xsib-hr` servisining `leave/` qismi.
//
//   GET    /api/hr/leave-types?activeOnly=                    hr.leave.view
//   POST   /api/hr/leave-types                                hr.leavetype.write
//   PUT    /api/hr/leave-types/{id}                           hr.leavetype.write
//
//   GET    /api/hr/leave-requests?personId=&state=&from=&to=  hr.leave.view
//   GET    /api/hr/leave-requests/{id}                        hr.leave.view
//   GET    /api/hr/persons/{id}/leave-balance?year=           hr.leave.view
//   POST   /api/hr/persons/{id}/leave-requests                hr.leave.request
//   POST   /api/hr/leave-requests/{id}/approve                hr.leave.approve
//   POST   /api/hr/leave-requests/{id}/reject                 hr.leave.approve
//   POST   /api/hr/leave-requests/{id}/cancel                 hr.leave.request
//   POST   /api/hr/persons/{id}/leave-adjustments             hr.leave.approve
//
// =====================================================================
// ⚠️ 1-QOIDA — SANA CHEGARASI TENANT ZONASIDA HISOBLANADI
// =====================================================================
// `startsOn` va `endsOn` — `LocalDate`, ya'ni KALENDAR kuni. Ularga
// zona konvertatsiyasi QO'LLANMAYDI va shuning uchun ekranda ham xom
// ko'rsatiladi (`hiredAt` bilan bir xil qoida).
//
// ⚠️ Lekin «bugun» qaysi kun ekani ZONAGA bog'liq. `dayjs()` brauzer
// zonasida ishlaydi: Toshkentdan boshqarilayotgan Kobul tenantida
// (UTC+4:30 va UTC+5:00) kuniga YARIM SOAT davomida ikkalasi ikki xil
// sanada turadi. Shuning uchun standart qiymatlar
// `todayInTenantZone()` dan olinadi ({@link defaultLeaveRange}) —
// `format.ts` dagi «KUN CHEGARASI» bo'limi bu farqni batafsil yozadi.
//
// =====================================================================
// ⚠️ 2-QOIDA — `pendingDays` QOLDIQDAN AYRILMAGAN
// =====================================================================
// `remainingDays = annualDays + adjustedDays - usedDays`, va `usedDays`
// FAQAT tasdiqlangan so'rovlarni sanaydi (`LeaveBalanceView` va
// `LeaveGuardIT` — «balans formulasi»). Ya'ni kutib turgan kunlar
// qoldiqqa KIRMAYDI.
//
// ⚠️ Panel buni YASHIRMAYDI: agar kutayotgani qoldiqdan ko'p bo'lsa,
// balansda ogohlantirish chiqadi. Aks holda operator «qoldiq 10 kun»
// deb ko'rib tasdiqlardi, server esa 409 bilan rad etardi — sababi
// ekranda ko'rinmagan holda.
//
// =====================================================================
// ⚠️ 3-QOIDA — RAD ETISHDA SABAB MAJBURIY, TASDIQLASHDA YO'Q
// =====================================================================
// `LeaveDecision.requireNote()` faqat rad etish yo'lida chaqiriladi:
// sababsiz rad etish 400 beradi. Tasdiqlashda izoh ixtiyoriy. Panelda
// ham aynan shunday — ikkalasiga bir xil qoida qo'yish operatorga
// keraksiz maydon to'ldirtirardi yoki teskarisi, serverdan tushunarsiz
// 400 keltirardi.
//
// =====================================================================
// ⚠️ 4-QOIDA — TA'TIL TURLARI KATALOGI EKILMAGAN va bu ATAYLAB
// =====================================================================
// Migratsiya birorta ta'til turini yaratmaydi (`xsib-hr/README.md` —
// «`annual_days` QONUNIY huquq. Raqam o'ylab topilsa balans jimgina
// noto'g'ri chiqardi»). Ya'ni yangi tashkilotda katalog BO'SH va
// birinchi so'rov 404 oladi. Panelning bo'sh holati shuni aytadi va
// katalogga yo'l ko'rsatadi — «hech narsa yo'q» deb qo'ymaydi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import { shiftDay, todayInTenantZone } from '@/shared/lib/format'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

/**
 * So'rov holati — `LeaveRequestState` enumining nusxasi.
 *
 * O'tishlar: `PENDING → APPROVED | REJECTED | CANCELLED`,
 * `APPROVED → CANCELLED`. `REJECTED` dan chiqish YO'Q.
 */
export const LEAVE_REQUEST_STATES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const
export type LeaveRequestState = (typeof LEAVE_REQUEST_STATES)[number]

// --- Ta'til turlari katalogi --------------------------------------------------

const leaveTypeSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  paid: z.boolean(),
  /** ⚠️ `null` = cheklovsiz tur (kasallik varaqasi kabi) */
  annualDays: z.number().nullish(),
  usesBalance: z.boolean(),
  requiresApproval: z.boolean(),
  active: z.boolean(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface LeaveType {
  id: string
  code: string
  name: string
  paid: boolean
  annualDays: number | null
  usesBalance: boolean
  requiresApproval: boolean
  active: boolean
  createdAt: string | null
  updatedAt: string | null
}

function toLeaveType(raw: z.infer<typeof leaveTypeSchema>): LeaveType {
  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    paid: raw.paid,
    annualDays: raw.annualDays ?? null,
    usesBalance: raw.usesBalance,
    requiresApproval: raw.requiresApproval,
    active: raw.active,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  }
}

export function parseLeaveType(raw: unknown): LeaveType {
  return toLeaveType(leaveTypeSchema.parse(raw))
}

export async function fetchLeaveTypes(activeOnly: boolean): Promise<readonly LeaveType[]> {
  const response = await api.get<unknown>('/api/hr/leave-types', { params: { activeOnly } })
  return z.array(leaveTypeSchema).parse(response.data).map(toLeaveType)
}

export interface LeaveTypeDraft {
  /** `^[A-Z][A-Z0-9_]{1,31}$` */
  code: string
  name: string
  paid: boolean
  /** `null` = cheklov yo'q. ⚠️ `usesBalance` yoqilgan bo'lsa MAJBURIY */
  annualDays: number | null
  usesBalance: boolean
  requiresApproval: boolean
  active: boolean
}

export async function createLeaveType(draft: LeaveTypeDraft): Promise<LeaveType> {
  const response = await api.post<unknown>('/api/hr/leave-types', draft)
  return parseLeaveType(response.data)
}

export async function updateLeaveType(id: string, draft: LeaveTypeDraft): Promise<LeaveType> {
  const response = await api.put<unknown>(`/api/hr/leave-types/${id}`, draft)
  return parseLeaveType(response.data)
}

// --- So'rovlar ---------------------------------------------------------------

const leaveRequestSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  personnelNo: z.string().nullish(),
  fullName: z.string().nullish(),
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string().nullish(),
  leaveTypeName: z.string().nullish(),
  paid: z.boolean(),
  /** `LocalDate`, ICHIGA KIRADI */
  startsOn: z.string(),
  /** `LocalDate`, ICHIGA KIRADI */
  endsOn: z.string(),
  /** ⚠️ Bazadagi hosila ustun — panelda QAYTA hisoblanmaydi */
  days: z.number(),
  state: z.enum(LEAVE_REQUEST_STATES),
  reason: z.string().nullish(),
  requestedBy: z.string().nullish(),
  requestedAt: instantSchema.nullish(),
  decidedBy: z.string().nullish(),
  decidedAt: instantSchema.nullish(),
  decisionNote: z.string().nullish(),
})

export interface LeaveRequest {
  id: string
  personId: string
  personnelNo: string | null
  fullName: string | null
  leaveTypeId: string
  leaveTypeCode: string | null
  leaveTypeName: string | null
  paid: boolean
  startsOn: string
  endsOn: string
  days: number
  state: LeaveRequestState
  reason: string | null
  requestedBy: string | null
  requestedAt: string | null
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
}

function toLeaveRequest(raw: z.infer<typeof leaveRequestSchema>): LeaveRequest {
  return {
    id: raw.id,
    personId: raw.personId,
    personnelNo: raw.personnelNo ?? null,
    fullName: raw.fullName ?? null,
    leaveTypeId: raw.leaveTypeId,
    leaveTypeCode: raw.leaveTypeCode ?? null,
    leaveTypeName: raw.leaveTypeName ?? null,
    paid: raw.paid,
    startsOn: raw.startsOn,
    endsOn: raw.endsOn,
    days: raw.days,
    state: raw.state,
    reason: raw.reason ?? null,
    requestedBy: raw.requestedBy ?? null,
    requestedAt: raw.requestedAt ?? null,
    decidedBy: raw.decidedBy ?? null,
    decidedAt: raw.decidedAt ?? null,
    decisionNote: raw.decisionNote ?? null,
  }
}

export function parseLeaveRequest(raw: unknown): LeaveRequest {
  return toLeaveRequest(leaveRequestSchema.parse(raw))
}

const leaveRequestPageSchema = z.object({
  rows: z.array(leaveRequestSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export function parseLeaveRequestPage(raw: unknown): PageResult<LeaveRequest> {
  const parsed = leaveRequestPageSchema.parse(raw)
  return { items: parsed.rows.map(toLeaveRequest), total: parsed.total }
}

export interface LeaveRequestFilter {
  personId: string | null
  state: LeaveRequestState | null
  /**
   * ⚠️ KESISHUV, ichiga olish EMAS (`LeaveRepository`):
   * `from` → `ends_on >= from`, `to` → `starts_on <= to`. Ya'ni oraliqqa
   * bir kuni tushgan so'rov ham ro'yxatga kiradi. Ekranda shu aytiladi —
   * aks holda operator «to'liq shu oyga tegishlilar» deb o'ylardi.
   */
  from: string | null
  to: string | null
}

export function fetchLeaveRequestPage(
  filter: LeaveRequestFilter,
): (state: TableState, signal: AbortSignal) => Promise<PageResult<LeaveRequest>> {
  return async (state, signal) => {
    const response = await api.get<unknown>('/api/hr/leave-requests', {
      signal,
      params: {
        ...(filter.personId === null ? {} : { personId: filter.personId }),
        ...(filter.state === null ? {} : { state: filter.state }),
        ...(filter.from === null ? {} : { from: filter.from }),
        ...(filter.to === null ? {} : { to: filter.to }),
        page: state.page - 1,
        size: state.pageSize,
      },
    })
    return parseLeaveRequestPage(response.data)
  }
}

/**
 * Bitta xodimning so'rovlari — kartochka uchun.
 *
 * ⚠️ `fetchLeaveRequestPage` DAN ALOHIDA va bu ataylab: u yerda
 * `DataTable` ning holati (sahifa, qidiruv, URL) bor, bu yerda esa
 * kartochka ichidagi qisqa ro'yxat. Jadval holatini soxta qiymat bilan
 * to'ldirib chaqirish ikkinchi «yashirin» sahifalash yaratardi va u
 * hech qayerda ko'rinmasdi.
 */
export async function fetchPersonLeaveRequests(
  personId: string,
  size = 50,
): Promise<readonly LeaveRequest[]> {
  const response = await api.get<unknown>('/api/hr/leave-requests', {
    params: { personId, page: 0, size },
  })
  return parseLeaveRequestPage(response.data).items
}

export async function fetchLeaveRequest(id: string): Promise<LeaveRequest> {
  const response = await api.get<unknown>(`/api/hr/leave-requests/${id}`)
  return parseLeaveRequest(response.data)
}

export interface LeaveRequestDraft {
  leaveTypeId: string
  /** "YYYY-MM-DD", ICHIGA KIRADI */
  startsOn: string
  /** "YYYY-MM-DD", ICHIGA KIRADI */
  endsOn: string
  /** ⚠️ MAJBURIY */
  reason: string
}

export async function requestLeave(
  personId: string,
  draft: LeaveRequestDraft,
): Promise<LeaveRequest> {
  const response = await api.post<unknown>(`/api/hr/persons/${personId}/leave-requests`, draft)
  return parseLeaveRequest(response.data)
}

/** ⚠️ Izoh IXTIYORIY */
export async function approveLeave(id: string, note: string | null): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/hr/leave-requests/${id}/approve`, { note })
  return operationResultSchema.parse(response.data)
}

/** ⚠️ Izoh MAJBURIY — bo'sh yuborilsa server 400 beradi */
export async function rejectLeave(id: string, note: string): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/hr/leave-requests/${id}/reject`, { note })
  return operationResultSchema.parse(response.data)
}

/** ⚠️ Tanasi YO'Q va huquqi `hr.leave.request` — xodim o'zinikini bekor qiladi */
export async function cancelLeave(id: string): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/hr/leave-requests/${id}/cancel`)
  return operationResultSchema.parse(response.data)
}

// --- Balans ------------------------------------------------------------------

const leaveBalanceSchema = z.object({
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string().nullish(),
  leaveTypeName: z.string().nullish(),
  usesBalance: z.boolean(),
  year: z.number(),
  /** ⚠️ `null` = cheklovsiz tur */
  entitledDays: z.number().nullish(),
  /** Qo'lda tuzatish yig'indisi — MANFIY ham bo'lishi mumkin */
  adjustedDays: z.number(),
  /** FAQAT tasdiqlangan so'rovlar */
  usedDays: z.number(),
  /** ⚠️ Qoldiqdan AYRILMAGAN */
  pendingDays: z.number(),
  /** ⚠️ `null` = cheklovsiz tur */
  remainingDays: z.number().nullish(),
})

export interface LeaveBalance {
  leaveTypeId: string
  leaveTypeCode: string | null
  leaveTypeName: string | null
  usesBalance: boolean
  year: number
  entitledDays: number | null
  adjustedDays: number
  usedDays: number
  pendingDays: number
  remainingDays: number | null
}

function toLeaveBalance(raw: z.infer<typeof leaveBalanceSchema>): LeaveBalance {
  return {
    leaveTypeId: raw.leaveTypeId,
    leaveTypeCode: raw.leaveTypeCode ?? null,
    leaveTypeName: raw.leaveTypeName ?? null,
    usesBalance: raw.usesBalance,
    year: raw.year,
    entitledDays: raw.entitledDays ?? null,
    adjustedDays: raw.adjustedDays,
    usedDays: raw.usedDays,
    pendingDays: raw.pendingDays,
    remainingDays: raw.remainingDays ?? null,
  }
}

export function parseLeaveBalance(raw: unknown): LeaveBalance {
  return toLeaveBalance(leaveBalanceSchema.parse(raw))
}

/**
 * @param year MAJBURIY — server bo'sh qolsa O'Z yilini qo'yadi
 *             (`LeaveController`), u esa UTC bo'yicha va 31-dekabrda
 *             tenant yili bilan farq qilishi mumkin
 */
export async function fetchLeaveBalance(
  personId: string,
  year: number,
): Promise<readonly LeaveBalance[]> {
  const response = await api.get<unknown>(`/api/hr/persons/${personId}/leave-balance`, {
    params: { year },
  })
  return z.array(leaveBalanceSchema).parse(response.data).map(toLeaveBalance)
}

export interface LeaveAdjustmentDraft {
  leaveTypeId: string
  year: number
  /** ⚠️ NOLDAN FARQLI: musbat — qo'shish, manfiy — ayirish */
  days: number
  reason: string
}

/** ⚠️ Javob — TUZATILGAN BALANS qatori, tuzatishning o'zi emas */
export async function adjustLeaveBalance(
  personId: string,
  draft: LeaveAdjustmentDraft,
): Promise<LeaveBalance> {
  const response = await api.post<unknown>(
    `/api/hr/persons/${personId}/leave-adjustments`,
    draft,
  )
  return parseLeaveBalance(response.data)
}

// --- Sof mantiq --------------------------------------------------------------

/**
 * Ikki chegara orasidagi KALENDAR kunlar soni, ikkalasi ham ichiga
 * kiradi: `2026-08-01 .. 2026-08-03` → 3.
 *
 * ⚠️ ISH KUNI EMAS. Bayram kalendari yo'q (§20.8) va dam olish kunini
 * taxmin qilish Afg'onistonda XATO bo'lardi — u yerda dam olish
 * payshanba-juma. Server ham aynan shunday sanaydi
 * (`LeaveRequestDraft#days`), ya'ni panel ko'rsatgan son bilan
 * yozilgan son bir xil bo'ladi.
 *
 * ⚠️ Hisob UTC yarim tunlari ustida yuritiladi va `Date` ning zona
 * arifmetikasiga tegmaydi: `LocalDate` da zona umuman yo'q, brauzer
 * zonasini aralashtirish esa kunni bir birlikka siljitardi.
 */
export function leaveDays(startsOn: string, endsOn: string): number {
  const start = Date.parse(`${startsOn}T00:00:00Z`)
  const end = Date.parse(`${endsOn}T00:00:00Z`)
  if (Number.isNaN(start) || Number.isNaN(end)) return 0
  const days = Math.round((end - start) / 86_400_000) + 1
  return days < 0 ? 0 : days
}

/** Qaror kutayotgan so'rovmi — tasdiqlash/rad etish tugmalari shunga qaraydi */
export function isPending(request: LeaveRequest): boolean {
  return request.state === 'PENDING'
}

/**
 * Bekor qilish mumkinmi.
 *
 * ⚠️ `APPROVED` ham bekor qilinadi (`LeaveServiceImpl`) — xodim
 * ta'tilga chiqmasa kunlari balansga qaytadi. `REJECTED` va
 * `CANCELLED` — yakuniy.
 */
export function canCancelLeave(request: LeaveRequest): boolean {
  return request.state === 'PENDING' || request.state === 'APPROVED'
}

/**
 * Kutayotgan kunlar qoldiqdan OSHIB KETDIMI.
 *
 * ⚠️ Bu ekrandagi yagona joy bo'lib, u serverning 409 ini OLDINDAN
 * aytadi: `remainingDays` kutayotgan kunlarni hisobga OLMAYDI, ya'ni
 * «qoldiq 10» ko'rinib turib tasdiqlash rad etilishi mumkin.
 * Cheklovsiz turda (`remainingDays === null`) bunday holat yo'q.
 */
export function pendingOverBalance(balance: LeaveBalance): boolean {
  if (!balance.usesBalance || balance.remainingDays === null) return false
  return balance.pendingDays > balance.remainingDays
}

/**
 * JORIY OY oralig'i, TENANT zonasida.
 *
 * ⚠️ `dayjs().startOf('month')` DAN ATAYLAB FARQ QILADI: u brauzer
 * zonasida ishlaydi va oyning birinchi/oxirgi yarim sutkasida oyni bir
 * oyga surib yuborardi. `todayInTenantZone` esa kunni tenant zonasida
 * beradi (`format.ts` dagi «KUN CHEGARASI» bo'limi).
 *
 * ⚠️ 2026-08-16 dan boshlab bu `LeaveRequestsPage` ning STANDART
 * qiymati EMAS. Sabab o'sha sahifa sarlavhasida o'lchov bilan
 * yozilgan (BUG-051): ta'til KELAJAKKA so'raladi va joriy oy oynasi
 * yangi so'rovni tasdiqlovchidan yashirardi. Funksiya oyni to'g'ri
 * hisoblaydi va shu holicha qoladi — u endi «bir oyni ko'rsat» degan
 * TANLOV uchun, standart uchun emas.
 */
export function defaultLeaveRange(today: string = todayInTenantZone()): {
  from: string
  to: string
} {
  const from = `${today.slice(0, 7)}-01`
  // Keyingi oyning 1-kunidan bir kun orqaga — oy uzunligini bilish shart emas
  const [year, month] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))]
  const nextMonth = month === 12 ? `${String(year + 1)}-01-01` : `${String(year)}-${String(month + 1).padStart(2, '0')}-01`
  return { from, to: shiftDay(nextMonth, -1) }
}

/** Balans ko'rsatiladigan standart yil — TENANT zonasidagi joriy yil */
export function defaultLeaveYear(today: string = todayInTenantZone()): number {
  return Number(today.slice(0, 4))
}
